import { Group } from "@mantine/core"
import { IconAlertTriangle, IconCheck } from "@tabler/icons-react"
import { Missing, Accordion, TextCell } from "@tfg/ui"
import type { TFunction } from "i18next"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EcobalyseAnalysisStatus } from "@tfg/types/schemas/enum"
import {
  EcobalyseErrorKeys,
  FindProductLcasResponse,
} from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/find-product-lcas.response.dto"

interface Props {
  analysis: FindProductLcasResponse["data"][number]["ecobalyseAnalysis"]
}

const SuccessStatusCell = ({ nbDaysAgo }: { nbDaysAgo: number }) => {
  const { t } = useTranslation("regulations")
  const successText = t("values.status.success", { count: nbDaysAgo })

  return (
    <Group gap={8} justify="flex-start" align="center" wrap="nowrap">
      <IconCheck color="green" />
      <TextCell value={successText} />
    </Group>
  )
}

const ErrorStatusCell = ({
  nbDaysAgo,
  errorKey,
  joinedArgs,
}: {
  nbDaysAgo: number
  errorKey: EcobalyseErrorKeys | undefined
  joinedArgs: string | undefined
}) => {
  const { t } = useTranslation("regulations")
  return (
    <Accordion>
      <Accordion.Item value="error">
        <Accordion.Control px={0}>
          <Group gap={8} justify="flex-start" align="center">
            <IconAlertTriangle color="gray" />
            <TextCell value={t("values.status.error", { count: nbDaysAgo })} />
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <TextCell value={getErrorMessageFromKey(errorKey, joinedArgs, t)} />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}

const getErrorMessageFromKey = (
  errorKey: EcobalyseErrorKeys | undefined,
  args: string | undefined,
  t: TFunction<"regulations", undefined>
): string => {
  switch (errorKey) {
    case EcobalyseErrorKeys.UNACCESSIBLE_ERROR:
      return t("values.lca.errors.unaccessible")
    case EcobalyseErrorKeys.MISSING_DATA_ERROR:
      return args
        ? t("values.lca.errors.missingData", { args })
        : t("values.lca.errors.default")
    case EcobalyseErrorKeys.UNSUPPORTED_DATA_ERROR:
      return args
        ? t("values.lca.errors.unsupportedData", { args })
        : t("values.lca.errors.default")
    default:
      return t("values.lca.errors.default")
  }
}

export const StatusCell = ({ analysis }: Props) => {
  // Calculate days ago - using useMemo to avoid calling Date.now() during render

  const nbDaysAgo = useMemo(() => {
    if (!analysis?.dateCreated) return 0
    const now = Date.now()
    const created = new Date(analysis.dateCreated).getTime()
    return Math.floor((now - created) / (1000 * 60 * 60 * 24))
  }, [analysis?.dateCreated])

  if (analysis.status === EcobalyseAnalysisStatus.SUCCESS) {
    return <SuccessStatusCell nbDaysAgo={nbDaysAgo} />
  } else if (analysis.status === EcobalyseAnalysisStatus.ERROR) {
    const errorKey = analysis.error?.errorKey
    const args = analysis.error?.args
    const joinedArgs = args?.join(", ")

    return (
      <ErrorStatusCell
        nbDaysAgo={nbDaysAgo}
        errorKey={errorKey}
        joinedArgs={joinedArgs}
      />
    )
  } else {
    return <Missing />
  }
}
